<script setup lang="ts">
import { computed } from "vue";
import { UiButton, UiCard, UiChip, UiText } from "@shopexperts/shared";
import type { PartnerBadge } from "@shopexperts/shared";
import { formatDateTime, listingStatusChip } from "./badgeListing";

const props = defineProps<{ badge: PartnerBadge; syncing?: boolean }>();
defineEmits<{ (e: "sync"): void }>();

const chip = computed(() => listingStatusChip(props.badge));
const listing = computed(() => props.badge.listing ?? null);
const sync = computed(() => props.badge.listing_sync ?? null);

const summary = computed(() => {
  const value = listing.value;
  if (!value) return "";
  const score = value.rating
    ? `★ ${value.rating.toFixed(1)}`
    : value.positive_review_percent !== null && value.positive_review_percent !== undefined
      ? `${value.positive_review_percent}% positive`
      : "";
  const reviews = value.review_count ? `${value.review_count.toLocaleString("en-US")} reviews` : "";
  return [score, reviews, value.pricing_label].filter(Boolean).join(" · ");
});

const lastSynced = computed(() => (sync.value?.last_synced_at ? `Last synced ${formatDateTime(sync.value.last_synced_at)}` : "Never synced"));
</script>

<template>
  <UiCard padding="sm">
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between gap-3">
        <UiChip v-if="chip" :text="chip.text" :variant="chip.variant" :tooltip="chip.tooltip" size="sm" />
        <UiButton variant="outline" :label="syncing ? 'Syncing…' : 'Sync now'" :disabled="syncing" @click="$emit('sync')" />
      </div>
      <div v-if="listing" class="flex flex-col gap-1">
        <UiText as="p" variant="p" class="font-semibold" :text="listing.name" />
        <UiText v-if="summary" as="p" variant="p-small" :text="summary" />
      </div>
      <UiText v-if="sync?.status === 'failed' && sync.error" as="p" variant="p-small" class="text-red-600" :text="sync.error.message" />
      <UiText as="p" variant="p-small" class="text-se-gray-500" :text="lastSynced" />
    </div>
  </UiCard>
</template>
